import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Code2, Copy, Download, ExternalLink, Webhook, Key, Zap } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CURL_QUICKSTART = `# 1. API-Key im Frontend anlegen unter
#    Einstellungen → Entwickler → API-Keys → "Neuer Key"
# 2. Token nutzen (nur 1× sichtbar!)

curl -H "Authorization: Bearer chk_live_xxxxxxxx" \\
     ${BACKEND_URL}/api/public/v1/ping

# Response:
# {
#   "ok": true,
#   "company_id": "...",
#   "key_name": "Treuhänder-Sync",
#   "permissions": ["read"],
#   "server_time": "2026-05-03T15:24:49Z"
# }`;

const CURL_LIST = `curl -H "Authorization: Bearer chk_live_xxx" \\
     "${BACKEND_URL}/api/public/v1/invoices?status=paid&limit=10"`;

const WEBHOOK_VERIFY_PY = `# Node.js / Express verify sample
import crypto from "crypto";

app.post("/webhook", (req, res) => {
  const sig = req.headers["x-signature"];           // "sha256=<hex>"
  const expected = "sha256=" + crypto
    .createHmac("sha256", WEBHOOK_SECRET)
    .update(JSON.stringify(req.body))
    .digest("hex");
  if (sig !== expected) return res.status(401).end();
  console.log("Event:", req.body.event, req.body.data);
  res.json({ok: true});
});`;

export function Developers() {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(false);

  useEffect(() => {
    apiClient
      .get("/public/v1/events")
      .then((r) => setEvents(r.data || []))
      .catch(() => setEvents([]));
    apiClient
      .get("/public/v1/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatusError(true));
  }, []);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Kopiert.");
  };

  const downloadPostman = () => {
    window.open(`${BACKEND_URL}/api/public/v1/postman-collection.json`, "_blank", "noopener,noreferrer");
  };

  const runInPostman = () => {
    const collectionUrl = `${BACKEND_URL}/api/public/v1/postman-collection.json`;
    const runUrl = `https://app.getpostman.com/run-collection/url?collection=${encodeURIComponent(collectionUrl)}`;
    window.open(runUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="developers-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
          <Code2 className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Developer Portal</h1>
          <p className="text-sm text-slate-500">
            Öffentliche REST-API v1 + Webhooks für Treuhänder-Software, ERPs und Zapier/Make.com-Integrationen.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="developers-quicklinks">
        <Button variant="outline" size="sm" asChild data-testid="link-swagger">
          <a href={`${BACKEND_URL}/api/docs`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Swagger UI
          </a>
        </Button>
        <Button variant="outline" size="sm" asChild data-testid="link-redoc">
          <a href={`${BACKEND_URL}/api/redoc`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> ReDoc
          </a>
        </Button>
        <Button variant="outline" size="sm" asChild data-testid="link-openapi-json">
          <a href={`${BACKEND_URL}/api/openapi.json`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> openapi.json
          </a>
        </Button>
        <Button variant="outline" size="sm" onClick={downloadPostman} data-testid="btn-download-postman">
          <Download className="h-3.5 w-3.5 mr-1" /> Postman (JSON)
        </Button>
        <Button variant="outline" size="sm" onClick={runInPostman} data-testid="btn-run-in-postman">
          <ExternalLink className="h-3.5 w-3.5 mr-1" /> Run in Postman
        </Button>
        <Button variant="default" size="sm" asChild data-testid="link-settings-developer">
          <a href="/settings#section-developer">
            <Key className="h-3.5 w-3.5 mr-1" /> API-Key anlegen
          </a>
        </Button>
      </div>

      <StatusWidget status={status} hasError={statusError} />

      <Card className="p-5 border-indigo-200 bg-indigo-50/40" data-testid="quickstart-card">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="h-4 w-4 text-indigo-700" />
          <h2 className="text-sm font-semibold text-slate-900">Quickstart (30 Sekunden)</h2>
        </div>
        <div className="relative">
          <pre className="bg-slate-900 text-slate-100 rounded-md p-4 text-xs overflow-x-auto" data-testid="quickstart-code">
            {CURL_QUICKSTART}
          </pre>
          <Button
            size="sm"
            variant="ghost"
            className="absolute top-2 right-2 text-slate-300 hover:text-white"
            onClick={() => copy(CURL_QUICKSTART)}
            data-testid="copy-quickstart"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </Card>

      <Card className="p-5 border-slate-200" data-testid="endpoints-card">
        <h2 className="text-sm font-semibold text-slate-900 mb-3">Verfügbare Endpunkte</h2>
        <div className="space-y-2 text-sm">
          <EndpointRow method="GET" path="/api/public/v1/ping" perm="–" label="Auth-Check" />
          <EndpointRow method="GET" path="/api/public/v1/status" perm="öffentlich" label="Health + Webhook-Telemetrie" />
          <EndpointRow method="GET" path="/api/public/v1/events" perm="öffentlich" label="Event-Katalog" />
          <EndpointRow method="GET" path="/api/public/v1/receipts" perm="read" label="Belege auflisten" />
          <EndpointRow method="GET" path="/api/public/v1/invoices" perm="read" label="Kundenrechnungen" />
          <EndpointRow method="GET" path="/api/public/v1/bills" perm="read" label="Lieferantenrechnungen" />
          <EndpointRow method="GET" path="/api/public/v1/postman-collection.json" perm="öffentlich" label="Postman-Collection Download" />
        </div>

        <div className="mt-4 relative">
          <pre className="bg-slate-900 text-slate-100 rounded-md p-4 text-xs overflow-x-auto" data-testid="list-example-code">
            {CURL_LIST}
          </pre>
          <Button
            size="sm"
            variant="ghost"
            className="absolute top-2 right-2 text-slate-300 hover:text-white"
            onClick={() => copy(CURL_LIST)}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </Card>

      <Card className="p-5 border-slate-200" data-testid="webhooks-card">
        <div className="flex items-center gap-2 mb-3">
          <Webhook className="h-4 w-4 text-slate-700" />
          <h2 className="text-sm font-semibold text-slate-900">Webhooks</h2>
        </div>
        <p className="text-xs text-slate-600 mb-3">
          Abonniere Events unter <code>Einstellungen → Entwickler → Webhooks</code>. Signatur-Header: <code className="bg-slate-100 px-1 py-0.5 rounded">X-Signature: sha256=HMAC(secret, body)</code>.
          Bei 5xx-Fehlern im Receiver: 3 Versuche, exponential backoff (1s/2s).
        </p>

        <div className="space-y-1.5 mb-4" data-testid="events-list">
          {events.length === 0 ? (
            <div className="text-xs text-slate-400">Event-Katalog wird geladen …</div>
          ) : (
            events.map((ev) => (
              <div
                key={ev.event}
                className="flex items-center justify-between gap-2 border-b border-slate-100 pb-1.5 text-xs"
                data-testid={`event-row-${ev.event.replace(".", "-")}`}
              >
                <code className="font-mono bg-slate-50 px-2 py-0.5 rounded text-indigo-700">{ev.event}</code>
                <span className="text-slate-600 text-right flex-1 ml-3">{ev.description}</span>
              </div>
            ))
          )}
        </div>

        <div className="relative">
          <pre className="bg-slate-900 text-slate-100 rounded-md p-4 text-xs overflow-x-auto" data-testid="webhook-verify-code">
            {WEBHOOK_VERIFY_PY}
          </pre>
          <Button
            size="sm"
            variant="ghost"
            className="absolute top-2 right-2 text-slate-300 hover:text-white"
            onClick={() => copy(WEBHOOK_VERIFY_PY)}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </Card>

      <Card className="p-4 border-slate-200 bg-amber-50/30" data-testid="limits-card">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">Grenzen & Best Practices</h3>
        <ul className="text-xs text-slate-700 list-disc pl-5 space-y-0.5">
          <li><strong>Rate</strong>: aktuell keine harten Limits — bitte fair nutzen (≤ 60 req/min pro Key empfohlen).</li>
          <li><strong>Pagination</strong>: <code>limit ≤ 200</code>, <code>skip ≥ 0</code>.</li>
          <li><strong>Secret-Rotation</strong>: API-Keys und Webhook-Secrets werden nur 1× im Klartext ausgegeben.</li>
          <li><strong>Webhook-Retry</strong>: 3 Versuche bei 5xx/Transport-Fehlern; 4xx wird als final abgewiesen.</li>
          <li><strong>Scope</strong>: ein API-Key ist an <em>eine</em> Firma gebunden — für Treuhänder-Mandate pro Firma einen eigenen Key anlegen.</li>
        </ul>
      </Card>
    </div>
  );
}

function EndpointRow({ method, path, perm, label }) {
  const methodColors = {
    GET: "bg-emerald-100 text-emerald-700",
    POST: "bg-sky-100 text-sky-700",
    DELETE: "bg-rose-100 text-rose-700",
  };
  return (
    <div className="flex items-center gap-2 border-b border-slate-100 pb-1.5 text-xs" data-testid={`endpoint-${path}`}>
      <Badge className={`${methodColors[method] || "bg-slate-100"} font-mono`}>{method}</Badge>
      <code className="font-mono text-slate-900">{path}</code>
      <span className="text-slate-500 ml-auto">{label}</span>
      <Badge variant="outline" className="text-[10px]">{perm}</Badge>
    </div>
  );
}

function StatusWidget({ status, hasError }) {
  const fmt = (iso) => {
    if (!iso) return "nie";
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  if (hasError) {
    return (
      <Card className="p-4 border border-rose-200 bg-rose-50" data-testid="api-status-widget">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-500" data-testid="api-status-dot" />
          <span className="text-sm font-medium text-rose-700">API-Status nicht verfügbar</span>
        </div>
      </Card>
    );
  }

  const healthy = status?.healthy !== false;
  const dotClass = healthy ? "bg-emerald-500" : "bg-amber-500";
  const label = healthy ? "All systems operational" : "Teilweise beeinträchtigt";

  return (
    <Card className="p-4 border border-slate-200" data-testid="api-status-widget">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className={`h-2.5 w-2.5 rounded-full ${dotClass} relative`} data-testid="api-status-dot">
            <span className={`absolute inset-0 rounded-full ${dotClass} opacity-70 animate-ping`} />
          </span>
          <div>
            <div className="text-sm font-medium text-slate-900" data-testid="api-status-label">{label}</div>
            <div className="text-[10px] text-slate-500">
              API v{status?.api_version || "–"} · Server-Zeit: {fmt(status?.server_time)}
            </div>
          </div>
        </div>
        <div className="flex gap-2 text-xs">
          <StatCell label="Subscriptions" value={status?.webhook_stats?.total_subscriptions ?? 0} testid="stat-subscriptions" />
          <StatCell label="aktiv (24h)" value={status?.webhook_stats?.active_last_24h ?? 0} tone="emerald" testid="stat-active-24h" />
          <StatCell label="failing" value={status?.webhook_stats?.failing_now ?? 0} tone={(status?.webhook_stats?.failing_now ?? 0) > 0 ? "rose" : "slate"} testid="stat-failing" />
          <StatCell label="letzte Delivery" value={fmt(status?.webhook_stats?.last_delivery_at)} testid="stat-last-delivery" mono={false} />
        </div>
      </div>
    </Card>
  );
}

function StatCell({ label, value, tone = "slate", testid, mono = true }) {
  const toneClasses = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    rose: "text-rose-600",
  };
  return (
    <div className="text-center px-2" data-testid={testid}>
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${toneClasses[tone] || toneClasses.slate} text-sm font-semibold`}>{value}</div>
    </div>
  );
}

export default Developers;
